import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { 
  ArrowLeft, PieChart, TrendingUp, Shield, Zap, Target, 
  BarChart3, Layers, Play, RefreshCw, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Check
} from 'lucide-react';

const STRATEGY_INFO = {
  orbis_alpha: { name: 'Orbis Alpha', icon: TrendingUp, color: '#2F6BFF', risk: 'medium' },
  orbis_momentum: { name: 'Orbis Momentum', icon: Zap, color: '#EF4444', risk: 'high' },
  orbis_defensive: { name: 'Orbis Defensive', icon: Shield, color: '#22C55E', risk: 'low' },
  orbis_fibonacci: { name: 'Orbis Fibonacci', icon: Target, color: '#F59E0B', risk: 'medium' },
  orbis_pattern: { name: 'Orbis Pattern', icon: BarChart3, color: '#8B5CF6', risk: 'medium' },
  orbis_composite: { name: 'Orbis Composite', icon: Layers, color: '#06B6D4', risk: 'medium' },
};

const PortfolioSimulator = () => {
  const navigate = useNavigate();
  const [allocations, setAllocations] = useState({
    orbis_alpha: 25,
    orbis_momentum: 0,
    orbis_defensive: 25,
    orbis_fibonacci: 25,
    orbis_pattern: 0,
    orbis_composite: 25,
  });
  const [initialCapital, setInitialCapital] = useState(1000);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestedProfile, setSuggestedProfile] = useState(null);

  const CAPITAL_PRESETS = [100, 500, 1000, 5000, 10000, 50000];

  const totalAllocation = Object.values(allocations).reduce((a, b) => a + b, 0);
  const isValid = Math.abs(totalAllocation - 100) < 0.01;

  const handleAllocationChange = (strategyId, value) => {
    const numValue = Math.max(0, Math.min(100, Number(value) || 0));
    setAllocations(prev => ({
      ...prev,
      [strategyId]: numValue
    }));
  };

  const runSimulation = async () => {
    if (!isValid) {
      toast.error('Allocations must total 100%');
      return;
    }

    if (initialCapital < 10) {
      toast.error('Minimum capital is £10');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/portfolio/simulate', {
        allocations,
        days: 30,
        initial_capital: initialCapital
      });
      setSimulation(response.data);
      toast.success('Simulation complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestedAllocation = async (profile) => {
    setLoading(true);
    setSuggestedProfile(profile);
    try {
      const response = await api.get(`/portfolio/suggest/${profile}?capital=${initialCapital}`);
      setAllocations(response.data.suggested_allocation);
      setSimulation(response.data.simulation);
      toast.success(`Loaded ${profile} allocation`);
    } catch (error) {
      toast.error('Failed to load suggestion');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center">
          <Button
            data-testid="simulator-back-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Page Title */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#2F6BFF]/20 rounded-lg flex items-center justify-center">
              <PieChart className="w-6 h-6 text-[#2F6BFF]" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold font-['Outfit']">Portfolio Simulator</h1>
              <p className="text-[#94A3B8]">Test different strategy combinations before committing capital</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Allocation Panel */}
          <div>
            {/* Trading Capital Input */}
            <Card className="bg-[#15171C] border-[#1E293B] mb-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#2F6BFF]" />
                  Trading Capital
                </CardTitle>
                <CardDescription className="text-[#94A3B8]">
                  How much would you invest? See your projected returns.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Custom Input */}
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-[#2F6BFF]">£</span>
                  <Input
                    data-testid="capital-input"
                    type="number"
                    min="10"
                    max="10000000"
                    value={initialCapital}
                    onChange={(e) => setInitialCapital(Math.max(10, Number(e.target.value) || 0))}
                    className="text-2xl font-bold bg-[#0B0D12] border-[#1E293B] h-14"
                    placeholder="Enter amount"
                  />
                </div>
                
                {/* Preset Buttons */}
                <div className="flex flex-wrap gap-2">
                  {CAPITAL_PRESETS.map((amount) => (
                    <Button
                      key={amount}
                      data-testid={`capital-${amount}`}
                      onClick={() => setInitialCapital(amount)}
                      variant={initialCapital === amount ? 'default' : 'outline'}
                      className={initialCapital === amount 
                        ? 'bg-[#2F6BFF]' 
                        : 'border-[#1E293B] text-[#94A3B8] hover:text-[#F5F7FA]'
                      }
                      size="sm"
                    >
                      {formatCurrency(amount)}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Presets */}
            <Card className="bg-[#15171C] border-[#1E293B] mb-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-['Outfit']">Quick Presets</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  {['conservative', 'balanced', 'aggressive'].map((profile) => (
                    <Button
                      key={profile}
                      data-testid={`preset-${profile}`}
                      onClick={() => loadSuggestedAllocation(profile)}
                      variant={suggestedProfile === profile ? 'default' : 'outline'}
                      className={suggestedProfile === profile 
                        ? 'bg-[#2F6BFF]' 
                        : 'border-[#1E293B] text-[#94A3B8] hover:text-[#F5F7FA]'
                      }
                      size="sm"
                    >
                      {profile === 'conservative' && <Shield className="w-4 h-4 mr-1" />}
                      {profile === 'balanced' && <Layers className="w-4 h-4 mr-1" />}
                      {profile === 'aggressive' && <Zap className="w-4 h-4 mr-1" />}
                      {profile.charAt(0).toUpperCase() + profile.slice(1)}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Strategy Allocations */}
            <Card className="bg-[#15171C] border-[#1E293B]">
              <CardHeader>
                <CardTitle className="font-['Outfit']">Strategy Allocations</CardTitle>
                <CardDescription className="text-[#94A3B8]">
                  Adjust percentages for each strategy (must total 100%)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(STRATEGY_INFO).map(([strategyId, info]) => {
                  const Icon = info.icon;
                  return (
                    <div key={strategyId} className="flex items-center gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${info.color}20` }}
                        >
                          <Icon className="w-4 h-4" style={{ color: info.color }} />
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm text-[#F5F7FA]">{info.name}</Label>
                          <p className={`text-xs ${
                            info.risk === 'high' ? 'text-red-400' :
                            info.risk === 'low' ? 'text-green-400' : 'text-yellow-400'
                          }`}>{info.risk} risk</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 w-32">
                        <Input
                          data-testid={`allocation-${strategyId}`}
                          type="number"
                          min="0"
                          max="100"
                          value={allocations[strategyId]}
                          onChange={(e) => handleAllocationChange(strategyId, e.target.value)}
                          className="w-20 bg-[#0B0D12] border-[#1E293B] text-center"
                        />
                        <span className="text-[#94A3B8]">%</span>
                      </div>
                    </div>
                  );
                })}

                {/* Total Indicator */}
                <div className={`flex items-center justify-between p-3 rounded-lg ${
                  isValid ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <span className="text-sm font-medium">Total Allocation</span>
                  <span className={`font-bold ${isValid ? 'text-green-400' : 'text-red-400'}`}>
                    {totalAllocation}%
                  </span>
                </div>

                {/* Run Simulation Button */}
                <Button
                  data-testid="run-simulation-btn"
                  onClick={runSimulation}
                  disabled={!isValid || loading}
                  className="w-full bg-[#2F6BFF] hover:bg-[#2F6BFF]/90"
                  size="lg"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  {loading ? 'Simulating...' : 'Run 30-Day Simulation'}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div>
            {simulation ? (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="space-y-6"
              >
                {/* Main Results */}
                <Card className="bg-[#15171C] border-[#1E293B]">
                  <CardHeader>
                    <CardTitle className="font-['Outfit']">Simulation Results</CardTitle>
                    <CardDescription className="text-[#94A3B8]">
                      30-day projected performance with {formatCurrency(simulation.initial_capital || initialCapital)} capital
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Capital & Projected Profit Summary */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-[#0B0D12] rounded-lg p-4 text-center">
                        <p className="text-xs text-[#94A3B8] mb-1">Your Capital</p>
                        <p className="text-2xl font-bold text-[#2F6BFF]">
                          {formatCurrency(simulation.initial_capital || initialCapital)}
                        </p>
                      </div>
                      <div className="bg-[#0B0D12] rounded-lg p-4 text-center">
                        <p className="text-xs text-[#94A3B8] mb-1">Projected 30-Day Profit</p>
                        <p className={`text-2xl font-bold ${
                          simulation.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {simulation.total_pnl >= 0 ? '+' : ''}{formatCurrency(simulation.total_pnl)}
                        </p>
                      </div>
                    </div>

                    {/* Big Return Percentage */}
                    <div className="text-center mb-6 p-6 bg-gradient-to-r from-[#2F6BFF]/10 to-[#22C55E]/10 rounded-lg border border-[#2F6BFF]/30">
                      <p className="text-sm text-[#94A3B8] mb-2">Projected Return</p>
                      <div className={`text-5xl font-bold ${
                        simulation.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {simulation.total_pnl >= 0 ? '+' : ''}{simulation.total_pnl_percentage}%
                      </div>
                      <p className="text-sm text-[#94A3B8] mt-2">
                        {formatCurrency(simulation.initial_capital || initialCapital)} → {formatCurrency((simulation.initial_capital || initialCapital) + simulation.total_pnl)}
                      </p>
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-[#0B0D12] rounded-lg p-4">
                        <p className="text-xs text-[#94A3B8] mb-1">Win Rate</p>
                        <p className={`text-xl font-bold ${
                          simulation.win_rate >= 55 ? 'text-green-400' : 
                          simulation.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
                        }`}>{simulation.win_rate}%</p>
                      </div>
                      <div className="bg-[#0B0D12] rounded-lg p-4">
                        <p className="text-xs text-[#94A3B8] mb-1">Total Trades</p>
                        <p className="text-xl font-bold text-[#F5F7FA]">{simulation.total_trades}</p>
                      </div>
                      <div className="bg-[#0B0D12] rounded-lg p-4">
                        <p className="text-xs text-[#94A3B8] mb-1">Max Drawdown</p>
                        <p className={`text-xl font-bold ${
                          simulation.max_drawdown <= 10 ? 'text-green-400' :
                          simulation.max_drawdown <= 15 ? 'text-yellow-400' : 'text-red-400'
                        }`}>{simulation.max_drawdown}%</p>
                      </div>
                      <div className="bg-[#0B0D12] rounded-lg p-4">
                        <p className="text-xs text-[#94A3B8] mb-1">Sharpe Ratio</p>
                        <p className={`text-xl font-bold ${
                          simulation.sharpe_ratio >= 1.5 ? 'text-green-400' :
                          simulation.sharpe_ratio >= 1 ? 'text-yellow-400' : 'text-red-400'
                        }`}>{simulation.sharpe_ratio}</p>
                      </div>
                    </div>

                    {/* Diversification Benefit */}
                    {simulation.diversification_benefit > 0 && (
                      <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-green-400">
                          {simulation.diversification_benefit.toFixed(1)}% volatility reduction from diversification
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Strategy Breakdown */}
                <Card className="bg-[#15171C] border-[#1E293B]">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Outfit']">Strategy Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(simulation.strategy_breakdown || {}).map(([strategyId, data]) => {
                        const info = STRATEGY_INFO[strategyId];
                        const Icon = info?.icon || TrendingUp;
                        return (
                          <div key={strategyId} className="flex items-center justify-between p-3 bg-[#0B0D12] rounded-lg">
                            <div className="flex items-center gap-3">
                              <Icon className="w-4 h-4" style={{ color: info?.color }} />
                              <div>
                                <p className="text-sm text-[#F5F7FA]">{info?.name}</p>
                                <p className="text-xs text-[#94A3B8]">{data.allocation}% allocated</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`text-sm font-bold ${data.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {data.pnl >= 0 ? '+' : ''}{formatCurrency(data.pnl)}
                              </p>
                              <p className="text-xs text-[#94A3B8]">{data.contribution}% contribution</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <Card className="bg-[#15171C] border-[#1E293B] h-full flex items-center justify-center min-h-[400px]">
                <CardContent className="text-center">
                  <PieChart className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
                  <p className="text-[#94A3B8] mb-2">No simulation results yet</p>
                  <p className="text-sm text-[#64748B]">
                    Adjust your allocations and click "Run Simulation" to see projected results
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-[#15171C] border border-[#1E293B] rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#94A3B8]">
              These projections are based on simulated backtesting and historical patterns. 
              Past performance does not guarantee future results. Trading involves risk 
              and you should not invest money you cannot afford to lose.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSimulator;
